import { useState } from 'react';
import { type Control } from 'react-hook-form';
import {
  Box,
  CardSpinner,
  Column,
  FloatingContainer,
  Icon,
  Row,
  Screen,
  Text,
  ZStack,
} from '~/commonComponents';
import { BackButtonProxy as BackButton } from '~/commonComponents/BackButton';
import Button from '~/commonComponents/Button';
import { SectionCard } from '~/components/ShiftRequestRedesign/common';
import AnimatedCheckCircle from '~/components/shared/AnimatedCheckCircle';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { Colors } from '~/themes/colors';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { AutoConfirmField } from '../ShiftDetails/SpeedUpCoverage/AutoConfirmField';
import { DatePickerPopup } from './DatePickerPopup';
import { EditOpenShiftForm } from './EditOpenShiftForm';
import { EditableDatePill } from './EditableDatePill';
import { useEditOpenShift } from './useEditOpenShift';

export const EditOpenShiftScreen = () => {
  const {
    job,
    isLoadingJob,
    officeName,
    professionType,
    preferredSpecialty,
    control,
    watch,
    selectedDate,
    setSelectedDate,
    openField,
    toggleField,
    showSuccessPopup,
    showNavigationGuard,
    isSaving,
    handleSave,
    handleSuccessConfirm,
    handleBack,
    navigateToPending,
    clearPendingNavigation,
    setShowNavigationGuard,
  } = useEditOpenShift();

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  if (isLoadingJob || !job) return <CardSpinner />;

  return (
    <Screen minWidth={768} paddingInline={30} paddingTop={16} paddingBottom={16}>
      <FloatingContainer
        justifyContent="flex-start"
        maxWidth={1120}
        borderRadius={0}
        margin={0}
        minWidth={0}
        width="100%"
        alignSelf="center"
        height="100%"
      >
        <Row alignItems="center" marginTop={60} position="relative">
          <ZStack left={20}>
            <BackButton onClick={handleBack} />
          </ZStack>
          <Text bold fontSize={28} textAlign="center" flex={1}>
            Edit Shift
          </Text>
        </Row>

        <Column paddingHorizontal={28} paddingTop={8} paddingBottom={60}>
          <Row marginTop={32} gap={24} flexWrap="wrap">
            <Column flex={1} minWidth={430}>
              <SectionCard title="Shift Details" testId="edit-open-shift-details-card">
                <EditOpenShiftForm
                  control={control}
                  watch={watch}
                  officeName={officeName}
                  openField={openField}
                  toggleField={toggleField}
                />
              </SectionCard>
            </Column>

            <Column flex={1} minWidth={430}>
              <Box
                data-testid="edit-open-shift-date-card"
                data-cy="edit-open-shift-date-card"
                backgroundColor={Colors.white}
                borderRadius={8}
                padding="20px 28px"
                border={`1.5px solid ${Colors.neutral_100}`}
              >
                <Row alignItems="center" justifyContent="space-between">
                  <Text bold fontSize={24} color="neutral_600">
                    Date
                  </Text>
                  <Box position="relative">
                    <EditableDatePill
                      date={selectedDate}
                      onClick={() => setIsDatePickerOpen((v) => !v)}
                    />
                    <DatePickerPopup
                      currentDate={selectedDate}
                      isOpen={isDatePickerOpen}
                      onToggle={() => setIsDatePickerOpen(false)}
                      onConfirm={(date) => {
                        setSelectedDate(date);
                        setIsDatePickerOpen(false);
                      }}
                    />
                  </Box>
                </Row>
              </Box>

              <Box marginTop={24}>
                <SectionCard title="Speed Up Your Coverage" testId="edit-open-shift-speed-up-card">
                  <AutoConfirmField
                    control={control as unknown as Control<IShiftDetailsFormValues>}
                    professionType={professionType}
                    preferredSpecialty={preferredSpecialty}
                    eventContext="open_shift"
                  />
                </SectionCard>
              </Box>
            </Column>
          </Row>

          <Button
            text="Save Changes"
            data-testid="save-changes-button"
            dataCy="save-changes-button"
            onClick={handleSave}
            isLoading={isSaving}
            style={{
              width: '100%',
              maxWidth: 296,
              margin: '40px auto 0',
              display: 'block',
              backgroundColor: Colors.primary_500,
            }}
          />
        </Column>
      </FloatingContainer>

      {showNavigationGuard && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <Box marginBottom={20}>
              <Icon name="warning-outline" size={80} />
            </Box>
          )}
          title="Are You Sure You Want to Leave?"
          description="You've made changes to your shift request that haven't been saved. Do you still want to leave?"
          leftButtonText="Go Back"
          leftButtonAction={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          rightButtonText="Leave Anyway"
          rightButtonAction={navigateToPending}
          closePopup={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          dataTestId="navigation-guard-popup"
        />
      )}

      {showSuccessPopup && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => <AnimatedCheckCircle />}
          titleStyle={{ marginTop: 30 }}
          description="The offer has been updated"
          descriptionStyle={{ width: '90%', marginBottom: 32, marginTop: 24 }}
          rightButtonText="Ok"
          rightButtonAction={handleSuccessConfirm}
          closePopup={handleSuccessConfirm}
          dataTestId="success-popup"
        />
      )}
    </Screen>
  );
};
